import { ChipsideComponent } from "../chipside-component";

export class SortableList extends ChipsideComponent {
	constructor() {
		super();

		this.ordered = this.hasAttribute("ordered");
		this.listClass = this.getAttribute("list-class") ?? "";
		this.listItemClass = this.getAttribute("listitem-class") ?? "";
		this.identifier = this.getAttribute("identifier") ?? "";
		this.listStyle = this.getAttribute("list-style");

		this.Options = {
			Animation: {
				Speed: 200,
				Easing: "ease-out"
			}
		};

		this.classList.add("sortable-list__wrapper");
	}

	parsedCallback() {
		const list = document.createElementWithContents(this.ordered ? "ol" : "ul", "", {
			className: this.listClass,
			ontouchstart: ev => this.#DragStart(ev),
			onmousedown: ev => this.#DragStart(ev)
		});

		if (this.identifier) { list.id = this.identifier; }
		if (this.listStyle) { list.classList.add(`list-${this.listStyle}`); }

		list.classList.add("sortable-list");

		for (const item of this.querySelectorAll(":scope > chip-sortablelistitem")) {
			this.AddItem(item, list);
		}

		this.appendChild(list);
	}

	AddItem(item, list) {
		const listItem = document.createElementWithContents("li", item),
			itemID = item.getAttribute("identifier");

		if (itemID) { listItem.id = itemID; }
		if (this.listItemClass) { listItem.classList.add(this.listItemClass); }

		(list ?? this.querySelector("ul,ol")).appendChild(listItem);
	}

	#DragStart(event) {
		if (this.Animation) { return; }
		if (event.type == "mousedown" && event.which !== 1) { return; }
		if (event.type === "touchstart" && e.touches.length > 1) { return; }

		const Hierarchy = {
			Handle: event.target.closest(".sortable-handle"),
			ListItem: event.target.closest(".sortable-list > li"),
			List: this.querySelector("ul,ol"),
			Items: [...this.querySelectorAll(":scope > * > li")]
		};

		if (!Hierarchy.Handle || !Hierarchy.ListItem) { return; }

		this.CurrentDraggable = Hierarchy.ListItem;

		Hierarchy.List.style.height = `${Hierarchy.List.offsetHeight}px`;
		Hierarchy.ListItem.classList.add("is-dragging");

		document.body.style.cursor = "grabbing";

		Object.assign(this, {
			ItemHeight: parseFloat(window.getComputedStyle(this.CurrentDraggable).height),
			ListHeight: Hierarchy.List.offsetHeight,
			StartTouchY: this.#GetDragY(event),
			StartTop: Hierarchy.ListItem.offsetTop
		});

		Hierarchy.Items.forEach((item, index) => {
			Object.assign(item.style, {
				position: "absolute",
				top: "-1px",
				left: `${parseFloat(window.getComputedStyle(Hierarchy.List).paddingInlineStart)}px`,
				width: "100%",
				transform: `translateY(${index === 0 ? 0 : Array(Math.max(0, index - 1) - 0 + 1).fill().map((_, idx) => 0 + idx).map(x => parseFloat(window.getComputedStyle(Hierarchy.Items[x]).height)).reduce((x, y) => x + y, 0)}px)`,
				zIndex: item === Hierarchy.ListItem ? 2 : 1
			});
		});

		setTimeout(() => {
			Hierarchy.Items.forEach((x) => {
				x.style.transition = `transform ${this.Options.Animation.Speed}ms ${this.Options.Animation.Easing}`;
			});
		});

		Object.assign(this, {
			Positions: Hierarchy.Items.map((_, i) => i),
			Position: Math.round(this.StartTop / this.ListHeight * Hierarchy.Items.length),
			Touch: event.type === "touchstart"
		});

		if (this.Touch) {
			Object.assign(window, {
				ontouchmove: ev => this.#DragMove(ev),
				ontouchend: ev => this.#DragEnd(ev)
			});
		} else {
			Object.assign(window, {
				onmousemove: ev => this.#DragMove(ev),
				onmouseup: ev => this.#DragEnd(ev)
			});
		}
	}

	#SwapElements(array, a, b) {
		const temp = array[a];

		array[a] = array[b];
		array[b] = temp;
	}

	#GetDragY(event) { return event.touches ? (event.touches[0] || event.changedTouches[0]).pageY : event.pageY; }

	#DragMove(event) {
		if (this.Animation) { return; }

		const items = this.querySelectorAll(":scope > * > li");

		const top = this.StartTop + this.#GetDragY(event) - this.StartTouchY,
			newPosition = Math.round((top / this.ListHeight) * items.length);

		this.CurrentDraggable.style.transform = `translateY(${top}px)`;

		this.Positions.forEach(index => {
			if (index === this.Position || index !== newPosition) { return; } // Can't replace with .filter because of the index

			this.#SwapElements(this.Positions, this.Position, index);
			this.Position = index;
		});

		items.forEach((item, index) => {
			if (item === this.Item) { return; } // Can't replace with .filter because of the index

			item.style.transform = `translateY(${this.Positions.indexOf(index) * this.ItemHeight}px)`;
		});

		event.preventDefault();

		event.target.closest("chip-sortablelistitem")?.raiseEvent("drag");
	}

	#DragEnd(event) {
		this.Animation = true;
		document.body.style.removeProperty("cursor");

		const list = this.querySelector("ul,ol"),
			items = list.querySelectorAll(":scope > li");

		Object.assign(this.CurrentDraggable.style, {
			transition: `all ${this.Options.Animation.Speed}ms ${this.Options.Animation.Easing}`,
			transform: `translateY(${this.Position * this.ItemHeight}px)`
		});

		this.CurrentDraggable.removeClass("is-dragging");

		setTimeout(() => {
			Object.assign(list.style, {
				position: "",
				height: ""
			});

			if (list.style.cssText.trim() === "") {
				list.removeAttribute("style");
			}

			items.forEach(item => {
				Object.assign(item.style, {
					top: "",
					left: "",
					right: "",
					position: "",
					transform: "",
					transition: "",
					width: "",
					zIndex: ""
				});

				if (item.style.cssText.trim() === "") {
					item.removeAttribute("style");
				}
			});

			this.Positions.forEach(i => list.appendChild(items[i]));

			this.Animation = false;

			event.target.closest("chip-sortablelistitem")?.raiseEvent("dragend");
		}, this.Options.Animation.Speed);

		Object.assign(window, {
			ontouchmove: null,
			ontouchend: null,
			onmousemove: null,
			onmouseup: null
		});
	}
}